/* src/ui/windows/placeCart.ts — setting the cart down.
 *
 * One decision, made on the map: **where**. The cart is in the player's bag until they click a
 * pedestrian way, and what it sells is chosen afterwards in the cart's own panel — a decision
 * about the cart belongs at the cart, not in a wizard the player is clicking through before
 * anything exists.
 *
 * The validity check is the server's, not ours. `check_placement` returns the exact point the
 * cart would snap to, so the banner reports the real outcome rather than a guess the server
 * might later disagree with. Hover probes are debounced and last-write-wins: the pointer moves
 * far faster than a round trip, and a stale reply must never overwrite a newer one.
 */

import { on } from '../../core/events.ts';
import { gameStore } from '../../core/store.ts';
import { checkPlacement, placeCart, type PlacementProbe } from '../../net/gameApi.ts';
import { incomeInterpolator } from '../../sim/incomeInterpolator.ts';
import { el, text } from '../dom.ts';
import { toast } from '../toast.ts';

/** Pointer moves are cheap; round trips are not. */
const PROBE_MS = 120;

export interface PlacementFlow {
  readonly el: HTMLElement;
  destroy: () => void;
}

export function startPlacement(
  root: HTMLElement,
  setPlacementMode: (on: boolean) => void,
): PlacementFlow {
  let probeTimer = 0;
  let probeSeq = 0;
  let busy = false;
  let destroyed = false;

  const status = text('Move over the city and click a footpath to set up.');
  const banner = el('div', { class: 'gf-place-banner' }, status);

  const panel = el(
    'aside',
    { class: 'gf-place' },
    el('h2', { class: 'gf-place-title' }, 'In your bag'),
    // The cart reads as a thing you are carrying, because that is exactly its state: it exists,
    // it is yours, and it is not on the map yet.
    el(
      'div',
      { class: 'gf-place-item' },
      el('span', { class: 'gf-place-icon' }, '🛒'),
      el(
        'span',
        { class: 'gf-place-itemid' },
        el('b', null, 'Food Cart'),
        el('em', null, 'not set up yet'),
      ),
    ),
    el(
      'p',
      { class: 'gf-place-sub' },
      'Set it down on any footpath, square or pedestrian street — anywhere people walk. ' +
        'You pick what it sells once it is up.',
    ),
    banner,
  );
  root.appendChild(panel);
  setPlacementMode(true);

  const setValid = (ok: boolean): void => {
    banner.classList.toggle('is-ok', ok);
    banner.classList.toggle('is-bad', !ok);
  };

  const offHover = on('world:placehover', ({ lon, lat }) => {
    if (destroyed || busy) return;
    window.clearTimeout(probeTimer);
    probeTimer = window.setTimeout(() => {
      const seq = ++probeSeq;
      void checkPlacement(lon, lat)
        .then((probe: PlacementProbe) => {
          // Last write wins: an older reply arriving late must not clobber a newer one.
          if (destroyed || busy || seq !== probeSeq) return;
          setValid(probe.ok);
          status.textContent = probe.ok
            ? `Good pitch — ${probe.wayName ?? probe.wayClass ?? 'footpath'}. Click to set up here.`
            : 'No footpath here. Try a pavement, square or pedestrian street.';
        })
        .catch(() => {
          /* A failed probe just leaves the last verdict up; the click still validates. */
        });
    }, PROBE_MS);
  });

  const offPick = on('world:placepick', ({ lon, lat }) => {
    if (destroyed || busy) return;
    const cart = gameStore.get()?.cart;
    if (!cart) return;

    busy = true;
    status.textContent = 'Setting up…';
    void checkPlacement(lon, lat)
      .then((probe) => {
        if (destroyed) return null;
        if (!probe.ok) {
          busy = false;
          setValid(false);
          status.textContent = 'That spot has no footpath. Pick somewhere people can walk.';
          return null;
        }
        return placeCart(cart.id, probe.lon ?? lon, probe.lat ?? lat);
      })
      .then((state) => {
        if (destroyed || !state) return;
        incomeInterpolator.sync(state);
        // The store update retires this panel and opens the cart, where the menu is chosen.
        gameStore.set(state);
      })
      .catch((err: unknown) => {
        busy = false;
        const msg = err instanceof Error ? err.message : 'Could not set up there.';
        status.textContent = msg;
        toast(msg);
      });
  });

  const flow: PlacementFlow = {
    el: panel,
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      window.clearTimeout(probeTimer);
      offHover();
      offPick();
      setPlacementMode(false);
      panel.remove();
    },
  };
  return flow;
}
